import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import io from 'socket.io-client';
import { burl } from '@/services/urls';
import useInterval from '@/utils/useInterval';
import { injectTimes } from '@/utils/inject-times';
import { useSocketStore } from '@/store/socket.store';

export const useLiveSocket = ({
	user,
	setUser,
	isMasterOrAdmin,
	activeOrdersRef,
	setActiveOrders,
	draftOrdersRef,
	setDraftOrders,
	sortOrders,
	allDeliveriesRef,
	setAllDeliveries,
	orderToAssignDeliveryRef,
	setOrderToAssignDelivery,
	setAssignDeliveryModalOpen,
}) => {
	const { socket, setSocket } = useSocketStore();

	const [positions, setPositions] = useState([]);

	// The socket effect runs once, so handlers close over the first render's `user`.
	// Everything that needs the current one reads it through this ref instead.
	const userRef = useRef(user);
	useEffect(() => {
		userRef.current = user;
	}, [user]);

	const askForPositions = () => {
		if (!socket) {
			return;
		}
		socket.emit('ask-positions', {
			cityId: user?.cityLocation?.id,
		});
	};

	useInterval(askForPositions, isMasterOrAdmin ? 5_000 : null, !!socket && user?.cityLocation?.id);

	const orderEvent = (event, options) => {
		const { order: o, deliveryId, orders } = options;

		if (event === 'order-add') {
			if (o.status === 'pe' || o.status === 'pi') {
				if (allDeliveriesRef.current?.length > 0) {
					const foundDeliveryIdx = allDeliveriesRef.current.findIndex(d => d.id === o.deliveryId);

					if (foundDeliveryIdx >= 0) {
						const newDeliveries = [...allDeliveriesRef.current];

						newDeliveries[foundDeliveryIdx] = {
							...newDeliveries[foundDeliveryIdx],
							activeOrdersCount: newDeliveries[foundDeliveryIdx].activeOrdersCount + 1,
						};

						setAllDeliveries(newDeliveries);
					}
				}
			}
		} else if (event === 'order-status-change') {
			if (!allDeliveriesRef.current?.length) {
				return;
			}
			const newDeliveries = allDeliveriesRef.current.map(d => {
				let ordCount = 0;
				orders.forEach(ord => {
					if (ord?.deliveryId === d.id) {
						ordCount++;
					}
				});
				return { ...d, activeOrdersCount: ordCount };
			});

			setAllDeliveries(newDeliveries);
		} else if (event === 'order-complete') {
			if (!allDeliveriesRef.current?.length) {
				return;
			}
			const foundDeliveryIdx = allDeliveriesRef.current.findIndex(d => d.id === deliveryId);

			if (foundDeliveryIdx >= 0) {
				const newDeliveries = [...allDeliveriesRef.current];
				const found = newDeliveries[foundDeliveryIdx];

				newDeliveries[foundDeliveryIdx] = {
					...found,
					activeOrdersCount:
						found.activeOrdersCount > 0 ? found.activeOrdersCount - 1 : found.activeOrdersCount,
					lastOrderCompletedAt: DateTime.now().toISO(),
				};

				setAllDeliveries(newDeliveries);
			}
		}
	};

	useEffect(() => {
		if (!user?.role) {
			return;
		}

		const setPos = data => {
			setPositions(data?.deliveries);
		};

		const prevSocket = useSocketStore.getState().socket;
		if (prevSocket) {
			prevSocket.disconnect();
		}

		let socket;
		if (isMasterOrAdmin) {
			socket = io(`${burl}/admin/${user?.cityLocation?.id}`);

			socket.on('positions-result', setPos);
		} else {
			socket = io(`${burl}/shop/${user?.cityLocation?.id}`);
		}

		setSocket(socket);

		const orderAdd = data => {
			if (!data?.shopId || !data?.order || (user.role === 'shop' && data.shopId != user.roleId)) {
				return;
			}

			if (
				typeof activeOrdersRef.current?.length !== 'number' ||
				(activeOrdersRef.current?.length > 0 &&
					activeOrdersRef.current.find(o => o.id == data?.order?.id))
			) {
				return;
			}

			orderEvent('order-add', { order: data.order });

			setActiveOrders(
				sortOrders([...activeOrdersRef.current, data.order].map(o => injectTimes(o))),
			);
		};

		socket.on('order-add', data => {
			orderAdd(data);
		});

		const draftOrderAdd = data => {
			if (!data?.shopId || !data?.order || (user.role === 'shop' && data.shopId != user.roleId)) {
				return;
			}

			if (
				typeof draftOrdersRef.current?.length !== 'number' ||
				(draftOrdersRef.current?.length > 0 &&
					draftOrdersRef.current.find(o => o.id == data?.order?.id))
			) {
				return;
			}

			setDraftOrders([...draftOrdersRef.current, data.order]);
		};

		socket.on('draft-order-add', data => {
			draftOrderAdd(data);
		});

		const orderStatusChange = data => {
			if (!data?.shopId || !data?.order || (user.role === 'shop' && data.shopId != user.roleId)) {
				return;
			}

			const orders = [...(activeOrdersRef.current || [])];

			const idx = orders.findIndex(o => o.id == data.order.id);

			if (idx === -1) {
				return;
			}

			orders[idx] = { ...data.order };

			const sortedOrders = sortOrders(orders.map(o => injectTimes(o)));

			orderEvent('order-status-change', {
				order: data.order,
				orders: sortedOrders,
			});

			setActiveOrders(sortedOrders);
		};

		socket.on('order-status-change', data => {
			orderStatusChange(data);
		});

		const orderConfirmAssign = data => {
			if (!data?.orderId || (user?.role !== 'admin' && user?.role !== 'master')) {
				return;
			}

			if (!activeOrdersRef.current?.length) {
				return;
			}

			const orders = [...activeOrdersRef.current];

			const idx = orders.findIndex(o => o.id == data.orderId);

			if (idx === -1) {
				return;
			}

			orders[idx] = { ...orders[idx], assignConfirmed: true };

			setActiveOrders(orders);
		};

		socket.on('order-confirm-assign', data => {
			orderConfirmAssign(data);
		});

		const orderComplete = data => {
			if (!data?.shopId || !data?.orderId || (user.role === 'shop' && data.shopId != user.roleId)) {
				return;
			}

			orderEvent('order-complete', { deliveryId: data.deliveryId });

			const orders = !activeOrdersRef.current?.length
				? []
				: [...activeOrdersRef.current].filter(o => o.id != data.orderId);

			if (orderToAssignDeliveryRef.current?.id == data.orderId) {
				setOrderToAssignDelivery(null);
				orderToAssignDeliveryRef.current = null;
				setAssignDeliveryModalOpen(false);
			}

			setActiveOrders(orders);
		};

		socket.on('order-complete', data => {
			orderComplete(data);
		});

		const orderCancel = data => {
			if (!data?.orderId) {
				return;
			}

			const orders = !activeOrdersRef.current?.length
				? []
				: [...activeOrdersRef.current].filter(o => o.id != data.orderId);

			setActiveOrders(orders);
		};

		socket.on('order-cancel', data => {
			orderCancel(data);
		});

		const wdOrderWebhook = data => {
			console.log('wdOrderWebhook', data);
			const { orderId, wolt_meta } = data || {};
			if (!orderId) {
				return;
			}

			if (!activeOrdersRef.current?.length) {
				return;
			}

			const foundOrderIdx = activeOrdersRef.current.findIndex(o => o.id === orderId);
			if (foundOrderIdx === -1) {
				return;
			}

			const newOrders = [...activeOrdersRef.current];

			newOrders[foundOrderIdx] = { ...newOrders[foundOrderIdx], wolt_meta };
			setActiveOrders(newOrders);
		};

		socket.on('wd-order-webhook', data => {
			wdOrderWebhook(data);
		});

		const clockEvent = data => {
			if (!data?.dId || !data?.newStatus) {
				return;
			}

			if (!allDeliveriesRef?.current?.length) {
				return;
			}

			const { dId } = data;
			const newDeliveries = [...allDeliveriesRef.current];
			const foundIndex = newDeliveries.findIndex(d => d.id == dId);
			if (!(foundIndex >= 0)) {
				return;
			}

			const { newStatus } = data;
			newDeliveries[foundIndex] = {
				...newDeliveries[foundIndex],
				isClockedIn: newStatus !== 'co',
				inBreak: newStatus === 'bi',
			};

			setAllDeliveries(newDeliveries);
		};

		socket.on('clock-event', data => {
			clockEvent(data);
		});

		socket.on('settings-change', data => {
			const currentUser = userRef.current;
			if (!currentUser) {
				return;
			}

			const newUser = {
				...currentUser,
				cityLocation: { ...currentUser.cityLocation, ...data },
			};

			setUser(newUser);

			console.log('new user: ', newUser);
		});
	}, []);

	return { positions, socket };
};
